import React from "react";
import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

const categoryContent = {
  werbung: {
    borderColor: "border-cyan-500",
    titleColor: "text-cyan-700",
    emoji: "🎯",
    title: "Werbung / Werbemaßnahme",
    description: "Produkte weitergeben, damit andere sie kennenlernen oder testen können.",
    example: "Beispiel: Dose als Probe an Interessenten verschenken.",
    warning: null,
    multiSection: false
  },
  geschenk: {
    borderColor: "border-rose-500",
    titleColor: "text-rose-700",
    emoji: "🎁",
    title: "Geschenk (geschäftlich)",
    description: "Produkte aus Dank oder Wertschätzung an bestimmte Personen verschenken.",
    example: "Beispiel: Dose als Dankeschön an treue Kundin.",
    warning: "⚠️ Maximal 50 € netto pro Person und Jahr.",
    multiSection: false
  },
  muster: {
    borderColor: "border-indigo-500",
    titleColor: "text-indigo-700",
    emoji: "🎪",
    title: "Muster / Eigenwerbung",
    description: "Produkte selbst bei Vorführungen, Infoabenden oder Verkostungen einsetzen.",
    example: "Beispiel: Dose öffnen und bei Präsentation probieren lassen.",
    warning: null,
    multiSection: false
  },
  privatnutzung: {
    borderColor: "border-slate-500",
    titleColor: "text-slate-700",
    emoji: "👤",
    title: "Privatnutzung",
    description: "Produkte privat verwenden oder ohne geschäftlichen Bezug verschenken.",
    example: null,
    warning: null,
    multiSection: false
  },
  rechnung: {
    multiSection: true,
    sections: [
      {
        borderColor: "border-emerald-500",
        titleColor: "text-emerald-700",
        emoji: "📄",
        title: "Normale Rechnung (ab 250 € brutto)",
        description: "Vollständige Rechnung mit allen Pflichtangaben und Kundendaten.",
        items: [
          "Vollständige Kundenadresse erforderlich",
          "Name, Straße, PLZ und Stadt müssen angegeben werden",
          "Alle gesetzlichen Pflichtangaben enthalten",
          "Für alle Rechnungen ab 250 € brutto (inkl. MwSt.)"
        ],
        warning: null
      },
      {
        borderColor: "border-blue-500",
        titleColor: "text-blue-700",
        emoji: "🧾",
        title: "Kleinbetragsrechnung (bis 250 € brutto)",
        description: "Vereinfachte Rechnung ohne Kundenadresse - nur Kundenname.",
        items: [
          "Nur Kundenname erforderlich",
          "Keine Adressangaben notwendig",
          "Vereinfachte Pflichtangaben ausreichend",
          "Nur für Rechnungen bis 250 € brutto (inkl. MwSt.)"
        ],
        warning: "💡 Die App wählt automatisch das richtige Format basierend auf dem Rechnungsbetrag."
      }
    ]
  }
};

export default function InfoModal({ isOpen, onClose, category }) {
  if (!isOpen) return null;

  const content = categoryContent[category];
  
  if (!content) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Info className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-slate-800">
                {content.multiSection ? "Rechnungsarten" : "Kategorie-Hilfe"}
              </h2>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {content.multiSection ? (
            <div className="space-y-6">
              {content.sections.map((section, index) => (
                <div key={index} className={`border-l-4 ${section.borderColor} pl-4`}>
                  <h3 className={`font-semibold ${section.titleColor} text-lg mb-2`}>
                    {section.emoji} {section.title}
                  </h3>
                  <p className="text-slate-600 mb-3">
                    {section.description}
                  </p>
                  {section.items && (
                    <ul className="space-y-1 mb-2">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start">
                          <span className="mr-2 text-slate-400">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.warning && (
                    <p className="text-sm font-medium text-blue-600 mt-2 bg-blue-50 p-2 rounded">
                      {section.warning}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={`border-l-4 ${content.borderColor} pl-4`}>
              <h3 className={`font-semibold ${content.titleColor} text-lg mb-2`}>
                {content.emoji} {content.title}
              </h3>
              <p className="text-slate-600 mb-2">
                {content.description}
              </p>
              {content.example && (
                <p className="text-sm text-slate-500 italic mb-1">
                  {content.example}
                </p>
              )}
              {content.warning && (
                <p className="text-sm font-medium text-rose-600">
                  {content.warning}
                </p>
              )}
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-slate-200">
            <Button onClick={onClose} className="w-full">
              Verstanden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}